import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  createUserWithEmailAndPassword,
  GoogleAuthProvider,
  getAuth,
  onAuthStateChanged,
  reload,
  sendEmailVerification,
  signInWithPopup,
  signInWithEmailAndPassword,
  signOut
} from 'firebase/auth';
import { API_BASE } from './apiConfig';
import { initFirebaseApp, isFirebaseConfigured } from './firebaseApp';
import { DEFAULT_WIDGET_THEME, WIDGET_SNIPPET } from './GamesBannerAdmin';
import './WidgetThemeStudio.css';

const FONT_OPTIONS = [
  { label: 'System UI', value: 'system-ui,-apple-system,"Segoe UI",Roboto,Helvetica,Arial,sans-serif' },
  { label: 'Sans Serif (Generic)', value: 'sans-serif' },
  { label: 'Arial', value: 'Arial, "Helvetica Neue", Helvetica, sans-serif' },
  { label: 'Helvetica', value: 'Helvetica, "Helvetica Neue", Arial, sans-serif' },
  { label: 'Segoe UI', value: '"Segoe UI", Tahoma, Geneva, Verdana, sans-serif' },
  { label: 'Roboto', value: 'Roboto, "Segoe UI", Helvetica, Arial, sans-serif' },
  { label: 'Tahoma', value: 'Tahoma, "Segoe UI", Arial, sans-serif' },
  { label: 'Verdana', value: 'Verdana, Geneva, Tahoma, sans-serif' },
  { label: 'Trebuchet MS', value: '"Trebuchet MS", Tahoma, Arial, sans-serif' },
  { label: 'Geneva', value: 'Geneva, Tahoma, Verdana, sans-serif' },
  { label: 'Lucida Sans', value: '"Lucida Sans", "Lucida Sans Unicode", "Lucida Grande", sans-serif' },
  { label: 'Calibri', value: 'Calibri, "Segoe UI", Arial, sans-serif' },
  { label: 'Candara', value: 'Candara, "Segoe UI", Arial, sans-serif' },
  { label: 'Optima', value: 'Optima, Candara, "Segoe UI", sans-serif' },
  { label: 'Gill Sans', value: '"Gill Sans", "Gill Sans MT", Calibri, sans-serif' },
  { label: 'Century Gothic', value: '"Century Gothic", "Trebuchet MS", Arial, sans-serif' },
  { label: 'Franklin Gothic', value: '"Franklin Gothic Medium", "Arial Narrow", Arial, sans-serif' },
  { label: 'Arial Narrow', value: '"Arial Narrow", Arial, sans-serif' },
  { label: 'Impact', value: 'Impact, Haettenschweiler, "Arial Narrow Bold", sans-serif' },
  { label: 'Serif (Generic)', value: 'serif' },
  { label: 'Georgia', value: 'Georgia, "Times New Roman", Times, serif' },
  { label: 'Times New Roman', value: '"Times New Roman", Times, serif' },
  { label: 'Cambria', value: 'Cambria, Georgia, "Times New Roman", serif' },
  { label: 'Palatino', value: '"Palatino Linotype", Palatino, "Book Antiqua", serif' },
  { label: 'Garamond', value: 'Garamond, "Times New Roman", serif' },
  { label: 'Book Antiqua', value: '"Book Antiqua", Palatino, Georgia, serif' },
  { label: 'Monospace (Generic)', value: 'monospace' },
  { label: 'Courier New', value: '"Courier New", Courier, monospace' },
  { label: 'Consolas', value: 'Consolas, "Liberation Mono", Menlo, Monaco, monospace' },
  { label: 'Lucida Console', value: '"Lucida Console", Monaco, monospace' },
  { label: 'Comic Sans', value: '"Comic Sans MS", "Comic Sans", cursive' }
];

const COLOR_FIELDS = [
  { key: 'bg', label: 'Background' },
  { key: 'card', label: 'Card background' },
  { key: 'line', label: 'Border/line' },
  { key: 'text', label: 'Main text' },
  { key: 'muted', label: 'Secondary text' },
  { key: 'accent', label: 'Accent' },
  { key: 'accentDark', label: 'Accent hover' },
  { key: 'cardHeaderBg', label: 'Game card header bg' },
  { key: 'cardHeaderText', label: 'Game card header text' },
  { key: 'cardHeaderStatus', label: 'Game card status text' },
  { key: 'headbarBg', label: 'Header background' },
  { key: 'headbarBorder', label: 'Header border' },
  { key: 'controlsBg', label: 'Filter/select background' },
  { key: 'controlsLabel', label: 'Competition label text' },
  { key: 'controlsText', label: 'Filter/select text' },
  { key: 'scrollThumb', label: 'Scrollbar thumb' }
];

const HEX_COLOR_PATTERN = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;
const sanitizeHexColor = (value, fallback) => {
  const candidate = String(value || '').trim();
  return HEX_COLOR_PATTERN.test(candidate) ? candidate : fallback;
};

const normalizeCompetitionList = (values = []) => {
  const rawValues = Array.isArray(values) ? values : [];
  const seen = new Set();
  const options = [];
  rawValues.forEach((entry) => {
    const label = String(entry || '').trim().replace(/\s+/g, ' ').slice(0, 120);
    if (!label) return;
    const key = label.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    options.push(label);
  });
  return options.sort((a, b) => a.localeCompare(b));
};

const resolveEmbedApiBase = () => {
  const base = API_BASE || '/api';
  if (/^https?:\/\//i.test(base)) {
    return base;
  }
  const normalized = String(base).startsWith('/') ? String(base) : `/${String(base)}`;
  if (typeof window !== 'undefined' && window.location?.origin) {
    return `${window.location.origin}${normalized}`;
  }
  return normalized;
};

const buildFeedUrl = () => `${String(resolveEmbedApiBase()).replace(/\/$/, '')}/games/banner`;

const HOUR_MS = 60 * 60 * 1000;
const DAY_MS = 24 * HOUR_MS;
const sampleTipoffFromNow = (offsetMs) => {
  const tipoffTs = Date.now() + offsetMs;
  return {
    tipoff: new Date(tipoffTs).toISOString(),
    tipoff_ts: tipoffTs
  };
};

const STUDIO_TEAM_LOGOS = {
  'B. Braun Sheffield Sharks': 'https://assets.zyrosite.com/m5Kw6GLDV6i64WP5/b._braun_sheffield_sharks-Iulq4RiEr0NlR3jV.png',
  'Besiktas GAIN Istanbul': 'https://assets.zyrosite.com/m5Kw6GLDV6i64WP5/besiktas_gain_istanbul-2SqgU8R1CyU9SN8r.png',
  'Bristol Flyers': 'https://assets.zyrosite.com/m5Kw6GLDV6i64WP5/bristol_flyers-J4uDccMN6V6m0avY.png',
  'Buducnost VOLI Podgorica': 'https://assets.zyrosite.com/m5Kw6GLDV6i64WP5/buducnost_voli_podgorica-xXnwtG1JKLGbSH2r.png',
  'Caledonia Gladiators': 'https://assets.zyrosite.com/m5Kw6GLDV6i64WP5/caledonia_gladiators-5IJTQOHczPAJWGUf.png',
  'Cheshire Phoenix': 'https://assets.zyrosite.com/m5Kw6GLDV6i64WP5/cheshire_phoenix-qeCXsUiT6wzXNmDn.png',
  'Cosea JL Bourg-en-Bresse': 'https://assets.zyrosite.com/m5Kw6GLDV6i64WP5/cosea_jl_bourg-en-bresse-RwUKBLmPiOyiSGwp.png',
  'Dolomiti Energia Trento': 'https://assets.zyrosite.com/m5Kw6GLDV6i64WP5/dolomiti_energia_trento-yy1Lm7Y1YUUSvkEW.png',
  'Leicester Riders': 'https://assets.zyrosite.com/m5Kw6GLDV6i64WP5/leicester_riders-apwdYH2BoyWFVgsx.png',
  'Lietkabelis Panevezys': 'https://assets.zyrosite.com/m5Kw6GLDV6i64WP5/lietkabelis_panevezys-I6a7ghit2NMKITwS.png',
  'London Lions': 'https://assets.zyrosite.com/m5Kw6GLDV6i64WP5/london_lions-lHw6c2viOGfYd2kU.png',
  'Manchester Basketball': 'https://assets.zyrosite.com/m5Kw6GLDV6i64WP5/manchester_basketball-W1NU6ZAdomVJetX9.png',
  'NINERS Chemnitz': 'https://assets.zyrosite.com/m5Kw6GLDV6i64WP5/niners_chemnitz-C9totHK0vr631LMa.png',
  'Newcastle Eagles': 'https://assets.zyrosite.com/m5Kw6GLDV6i64WP5/newcastle_eagles-QWxbN9u7qfilnkXn.png',
  'Panionios Athens': 'https://assets.zyrosite.com/m5Kw6GLDV6i64WP5/panionios-_athens-lif1ab3yjZWjFRUj.png',
  'Surrey 89ers': 'https://assets.zyrosite.com/m5Kw6GLDV6i64WP5/surrey_89ers-DjYsdndKqAPr8JFU.png',
  'Turk Telekom Ankara': 'https://assets.zyrosite.com/m5Kw6GLDV6i64WP5/turk_telekom_ankara-oEsABBMJMUYSvjjm.png',
  'ratiopharm Ulm': 'https://assets.zyrosite.com/m5Kw6GLDV6i64WP5/ratiopharm_ulm-erqgOQ43KKmLWPhq.png'
};

const buildStudioTeam = (name, score = 0) => ({
  name,
  logo: STUDIO_TEAM_LOGOS[name] || '',
  score
});

const createStudioGame = ({
  id,
  comp,
  status,
  statusDetail = '',
  home,
  away,
  homeScore = 0,
  awayScore = 0,
  offsetMs = 0,
  link = '',
  watchGame = '',
  ticketUrl = '',
  teaser = '',
  priority = 0
}) => ({
  id,
  comp,
  status,
  status_detail: statusDetail,
  home: buildStudioTeam(home, homeScore),
  away: buildStudioTeam(away, awayScore),
  link,
  watch_game: watchGame,
  ticket_url: ticketUrl,
  teaser,
  priority,
  ...sampleTipoffFromNow(offsetMs)
});

const STUDIO_SAMPLE_GAMES = [
  createStudioGame({
    id: 'studio-final-cup-1',
    comp: 'SLB Cup',
    status: 'FINAL',
    home: 'B. Braun Sheffield Sharks',
    away: 'Cheshire Phoenix',
    homeScore: 100,
    awayScore: 103,
    priority: 4,
    offsetMs: -25 * DAY_MS
  }),
  createStudioGame({
    id: 'studio-final-trophy-1',
    comp: 'SLB Trophy',
    status: 'FINAL',
    home: 'London Lions',
    away: 'Newcastle Eagles',
    homeScore: 88,
    awayScore: 81,
    priority: 3,
    offsetMs: -22 * DAY_MS
  }),
  createStudioGame({
    id: 'studio-final-eurocup-1',
    comp: 'EuroCup',
    status: 'FINAL',
    home: 'London Lions',
    away: 'Lietkabelis Panevezys',
    homeScore: 79,
    awayScore: 83,
    priority: 3,
    offsetMs: -20 * DAY_MS
  }),
  createStudioGame({
    id: 'studio-final-champ-1',
    comp: 'SLB Championship',
    status: 'FINAL',
    home: 'Leicester Riders',
    away: 'Manchester Basketball',
    homeScore: 91,
    awayScore: 84,
    priority: 3,
    offsetMs: -18 * DAY_MS
  }),
  createStudioGame({
    id: 'studio-final-eurocup-2',
    comp: 'EuroCup',
    status: 'FINAL',
    home: 'Besiktas GAIN Istanbul',
    away: 'London Lions',
    homeScore: 75,
    awayScore: 78,
    priority: 2,
    offsetMs: -16 * DAY_MS
  }),
  createStudioGame({
    id: 'studio-final-champ-2',
    comp: 'SLB Championship',
    status: 'FINAL',
    home: 'Bristol Flyers',
    away: 'Surrey 89ers',
    homeScore: 87,
    awayScore: 73,
    priority: 2,
    offsetMs: -14 * DAY_MS
  }),
  createStudioGame({
    id: 'studio-final-cup-2',
    comp: 'SLB Cup',
    status: 'FINAL',
    home: 'Manchester Basketball',
    away: 'Surrey 89ers',
    homeScore: 95,
    awayScore: 89,
    priority: 2,
    offsetMs: -12 * DAY_MS
  }),
  createStudioGame({
    id: 'studio-final-trophy-2',
    comp: 'SLB Trophy',
    status: 'FINAL',
    home: 'Cheshire Phoenix',
    away: 'Leicester Riders',
    homeScore: 82,
    awayScore: 90,
    priority: 2,
    offsetMs: -10 * DAY_MS
  }),
  createStudioGame({
    id: 'studio-final-eurocup-3',
    comp: 'EuroCup',
    status: 'FINAL',
    home: 'Turk Telekom Ankara',
    away: 'London Lions',
    homeScore: 80,
    awayScore: 76,
    priority: 2,
    offsetMs: -8 * DAY_MS
  }),
  createStudioGame({
    id: 'studio-final-champ-3',
    comp: 'SLB Championship',
    status: 'FINAL',
    home: 'Caledonia Gladiators',
    away: 'B. Braun Sheffield Sharks',
    homeScore: 77,
    awayScore: 86,
    priority: 1,
    offsetMs: -6 * DAY_MS
  }),
  createStudioGame({
    id: 'studio-final-trophy-3',
    comp: 'SLB Trophy',
    status: 'FINAL',
    home: 'Newcastle Eagles',
    away: 'B. Braun Sheffield Sharks',
    homeScore: 84,
    awayScore: 80,
    priority: 1,
    offsetMs: -4 * DAY_MS
  }),
  createStudioGame({
    id: 'studio-final-champ-4',
    comp: 'SLB Championship',
    status: 'FINAL',
    home: 'London Lions',
    away: 'Bristol Flyers',
    homeScore: 92,
    awayScore: 88,
    priority: 1,
    offsetMs: -2 * DAY_MS
  }),
  createStudioGame({
    id: 'studio-live-champ-1',
    comp: 'SLB Championship',
    status: 'LIVE',
    statusDetail: 'LIVE',
    home: 'Newcastle Eagles',
    away: 'Bristol Flyers',
    homeScore: 72,
    awayScore: 68,
    link: 'https://example.com/highlights/studio-live-champ-1',
    watchGame: 'https://example.com/watch/studio-live-champ-1',
    ticketUrl: 'https://example.com/tickets/studio-live-champ-1',
    teaser: 'Q4',
    priority: 10,
    offsetMs: -95 * 60 * 1000
  }),
  createStudioGame({
    id: 'studio-live-eurocup-1',
    comp: 'EuroCup',
    status: 'LIVE',
    statusDetail: 'LIVE',
    home: 'London Lions',
    away: 'NINERS Chemnitz',
    homeScore: 61,
    awayScore: 59,
    link: 'https://example.com/highlights/studio-live-eurocup-1',
    watchGame: 'https://example.com/watch/studio-live-eurocup-1',
    ticketUrl: 'https://example.com/tickets/studio-live-eurocup-1',
    teaser: 'Q3',
    priority: 9,
    offsetMs: -40 * 60 * 1000
  }),
  createStudioGame({
    id: 'studio-upcoming-champ-1',
    comp: 'SLB Championship',
    status: 'UPCOMING',
    home: 'Leicester Riders',
    away: 'London Lions',
    link: 'https://example.com/highlights/studio-upcoming-champ-1',
    watchGame: 'https://example.com/watch/studio-upcoming-champ-1',
    ticketUrl: 'https://example.com/tickets/studio-upcoming-champ-1',
    priority: 8,
    offsetMs: 2 * HOUR_MS
  }),
  createStudioGame({
    id: 'studio-upcoming-cup-1',
    comp: 'SLB Cup',
    status: 'UPCOMING',
    home: 'Cheshire Phoenix',
    away: 'London Lions',
    link: 'https://example.com/highlights/studio-upcoming-cup-1',
    watchGame: 'https://example.com/watch/studio-upcoming-cup-1',
    ticketUrl: 'https://example.com/tickets/studio-upcoming-cup-1',
    priority: 8,
    offsetMs: 5 * HOUR_MS
  }),
  createStudioGame({
    id: 'studio-upcoming-trophy-1',
    comp: 'SLB Trophy',
    status: 'UPCOMING',
    home: 'Newcastle Eagles',
    away: 'Cheshire Phoenix',
    link: 'https://example.com/highlights/studio-upcoming-trophy-1',
    watchGame: 'https://example.com/watch/studio-upcoming-trophy-1',
    ticketUrl: 'https://example.com/tickets/studio-upcoming-trophy-1',
    priority: 7,
    offsetMs: 9 * HOUR_MS
  }),
  createStudioGame({
    id: 'studio-upcoming-eurocup-1',
    comp: 'EuroCup',
    status: 'UPCOMING',
    home: 'London Lions',
    away: 'Panionios Athens',
    link: 'https://example.com/highlights/studio-upcoming-eurocup-1',
    watchGame: 'https://example.com/watch/studio-upcoming-eurocup-1',
    ticketUrl: 'https://example.com/tickets/studio-upcoming-eurocup-1',
    priority: 7,
    offsetMs: 14 * HOUR_MS
  }),
  createStudioGame({
    id: 'studio-upcoming-champ-2',
    comp: 'SLB Championship',
    status: 'UPCOMING',
    home: 'Bristol Flyers',
    away: 'Caledonia Gladiators',
    link: 'https://example.com/highlights/studio-upcoming-champ-2',
    watchGame: 'https://example.com/watch/studio-upcoming-champ-2',
    ticketUrl: 'https://example.com/tickets/studio-upcoming-champ-2',
    priority: 6,
    offsetMs: 1 * DAY_MS
  }),
  createStudioGame({
    id: 'studio-upcoming-champ-3',
    comp: 'SLB Championship',
    status: 'UPCOMING',
    home: 'Surrey 89ers',
    away: 'Manchester Basketball',
    link: 'https://example.com/highlights/studio-upcoming-champ-3',
    watchGame: 'https://example.com/watch/studio-upcoming-champ-3',
    ticketUrl: 'https://example.com/tickets/studio-upcoming-champ-3',
    priority: 6,
    offsetMs: 1 * DAY_MS + 6 * HOUR_MS
  }),
  createStudioGame({
    id: 'studio-upcoming-eurocup-2',
    comp: 'EuroCup',
    status: 'UPCOMING',
    home: 'Cosea JL Bourg-en-Bresse',
    away: 'London Lions',
    link: 'https://example.com/highlights/studio-upcoming-eurocup-2',
    watchGame: 'https://example.com/watch/studio-upcoming-eurocup-2',
    ticketUrl: 'https://example.com/tickets/studio-upcoming-eurocup-2',
    priority: 5,
    offsetMs: 2 * DAY_MS
  }),
  createStudioGame({
    id: 'studio-upcoming-trophy-2',
    comp: 'SLB Trophy',
    status: 'UPCOMING',
    home: 'London Lions',
    away: 'Bristol Flyers',
    link: 'https://example.com/highlights/studio-upcoming-trophy-2',
    watchGame: 'https://example.com/watch/studio-upcoming-trophy-2',
    ticketUrl: 'https://example.com/tickets/studio-upcoming-trophy-2',
    priority: 5,
    offsetMs: 3 * DAY_MS
  }),
  createStudioGame({
    id: 'studio-upcoming-cup-2',
    comp: 'SLB Cup',
    status: 'UPCOMING',
    home: 'B. Braun Sheffield Sharks',
    away: 'Cheshire Phoenix',
    link: 'https://example.com/highlights/studio-upcoming-cup-2',
    watchGame: 'https://example.com/watch/studio-upcoming-cup-2',
    ticketUrl: 'https://example.com/tickets/studio-upcoming-cup-2',
    priority: 4,
    offsetMs: 4 * DAY_MS
  }),
  createStudioGame({
    id: 'studio-upcoming-eurocup-3',
    comp: 'EuroCup',
    status: 'UPCOMING',
    home: 'Buducnost VOLI Podgorica',
    away: 'London Lions',
    link: 'https://example.com/highlights/studio-upcoming-eurocup-3',
    watchGame: 'https://example.com/watch/studio-upcoming-eurocup-3',
    ticketUrl: 'https://example.com/tickets/studio-upcoming-eurocup-3',
    priority: 4,
    offsetMs: 5 * DAY_MS
  }),
  createStudioGame({
    id: 'studio-upcoming-champ-4',
    comp: 'SLB Championship',
    status: 'UPCOMING',
    home: 'Newcastle Eagles',
    away: 'Leicester Riders',
    link: 'https://example.com/highlights/studio-upcoming-champ-4',
    watchGame: 'https://example.com/watch/studio-upcoming-champ-4',
    ticketUrl: 'https://example.com/tickets/studio-upcoming-champ-4',
    priority: 3,
    offsetMs: 6 * DAY_MS
  }),
  createStudioGame({
    id: 'studio-upcoming-trophy-3',
    comp: 'SLB Trophy',
    status: 'UPCOMING',
    home: 'Manchester Basketball',
    away: 'Leicester Riders',
    link: 'https://example.com/highlights/studio-upcoming-trophy-3',
    watchGame: 'https://example.com/watch/studio-upcoming-trophy-3',
    ticketUrl: 'https://example.com/tickets/studio-upcoming-trophy-3',
    priority: 3,
    offsetMs: 7 * DAY_MS
  })
];

const FALLBACK_REQUEST_COMPETITIONS = normalizeCompetitionList(
  STUDIO_SAMPLE_GAMES.map((game) => game.comp || game.competition || '')
);

const EMPTY_AUTH_FORM = {
  email: '',
  password: ''
};

const EMPTY_REQUEST_FORM = {
  website_url: '',
  embed_page_url: '',
  use_case: '',
  requested_competitions: []
};

const verificationActionCodeSettings = () => {
  if (typeof window === 'undefined') {
    return {};
  }
  return {
    url: window.location.href,
    handleCodeInApp: false
  };
};

const createGoogleProvider = () => {
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({ prompt: 'select_account' });
  return provider;
};

const resolvePrimaryProvider = (user) => {
  if (!user) return '';
  const providerIds = Array.isArray(user.providerData)
    ? user.providerData.map((entry) => entry?.providerId).filter(Boolean)
    : [];
  if (providerIds.includes('password')) {
    return 'password';
  }
  if (providerIds.includes('google.com')) {
    return 'google.com';
  }
  return providerIds[0] || '';
};

const WidgetThemeStudio = () => {
  const [theme, setTheme] = useState(DEFAULT_WIDGET_THEME);
  const [authForm, setAuthForm] = useState(EMPTY_AUTH_FORM);
  const [requestForm, setRequestForm] = useState(EMPTY_REQUEST_FORM);
  const [authConfigured] = useState(() => isFirebaseConfigured());
  const [firebaseUser, setFirebaseUser] = useState(null);
  const [authProvider, setAuthProvider] = useState('');
  const [emailVerified, setEmailVerified] = useState(false);
  const [authLoading, setAuthLoading] = useState(() => authConfigured);
  const [authBusy, setAuthBusy] = useState(false);
  const [authError, setAuthError] = useState('');
  const [authStatus, setAuthStatus] = useState('');
  const [requestSubmitting, setRequestSubmitting] = useState(false);
  const [requestSaving, setRequestSaving] = useState(false);
  const [requestDeleting, setRequestDeleting] = useState(false);
  const [requestError, setRequestError] = useState('');
  const [requestSuccess, setRequestSuccess] = useState('');
  const [myRequests, setMyRequests] = useState([]);
  const [myRequestsLoading, setMyRequestsLoading] = useState(false);
  const [myRequestsError, setMyRequestsError] = useState('');
  const [copiedRequestId, setCopiedRequestId] = useState(null);
  const [hydratedRequestToken, setHydratedRequestToken] = useState('');
  const [competitionOptions, setCompetitionOptions] = useState([]);
  const [competitionOptionsLoading, setCompetitionOptionsLoading] = useState(false);
  const [competitionOptionsError, setCompetitionOptionsError] = useState('');

  const authApp = useMemo(() => {
    if (!authConfigured) {
      return null;
    }
    return initFirebaseApp();
  }, [authConfigured]);

  const srcDoc = useMemo(() => {
    return WIDGET_SNIPPET('', buildFeedUrl(), theme, {
      preloadedGames: STUDIO_SAMPLE_GAMES,
      preloadedGamesOnly: true
    });
  }, [theme]);

  useEffect(() => {
    if (!authConfigured || !authApp) {
      setAuthLoading(false);
      return undefined;
    }
    const auth = getAuth(authApp);
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setFirebaseUser(user || null);
      setEmailVerified(Boolean(user?.emailVerified));
      setAuthProvider(resolvePrimaryProvider(user));
      setAuthLoading(false);
    });
    return () => {
      unsubscribe();
    };
  }, [authApp, authConfigured]);

  const updateTheme = (key, value) => {
    setTheme((prev) => ({ ...prev, [key]: value }));
  };

  const resetTheme = () => {
    setTheme(DEFAULT_WIDGET_THEME);
  };

  const getAuthInstance = () => {
    if (!authConfigured || !authApp) {
      const err = new Error('Authentication is not configured for this environment.');
      err.code = 'auth-not-configured';
      throw err;
    }
    return getAuth(authApp);
  };

  const runAuthAction = async (action) => {
    setAuthError('');
    setAuthStatus('');
    setAuthBusy(true);
    try {
      await action();
    } catch (err) {
      setAuthError(err?.message || 'Authentication action failed.');
    } finally {
      setAuthBusy(false);
    }
  };

  const handleCreateAccount = async (event) => {
    event.preventDefault();
    const email = authForm.email.trim();
    const password = authForm.password;
    if (!email || !password) {
      setAuthError('Email and password are required.');
      return;
    }
    await runAuthAction(async () => {
      const auth = getAuthInstance();
      const credential = await createUserWithEmailAndPassword(auth, email, password);
      await sendEmailVerification(credential.user, verificationActionCodeSettings());
      setAuthStatus(`Verification email sent to ${credential.user.email}.`);
      setEmailVerified(Boolean(credential.user.emailVerified));
      setAuthProvider(resolvePrimaryProvider(credential.user));
    });
  };

  const handleSignIn = async (event) => {
    event.preventDefault();
    const email = authForm.email.trim();
    const password = authForm.password;
    if (!email || !password) {
      setAuthError('Email and password are required.');
      return;
    }
    await runAuthAction(async () => {
      const auth = getAuthInstance();
      const credential = await signInWithEmailAndPassword(auth, email, password);
      setEmailVerified(Boolean(credential.user.emailVerified));
      setAuthProvider(resolvePrimaryProvider(credential.user));
      setAuthStatus(
        credential.user.emailVerified
          ? 'Signed in with a verified email address.'
          : 'Signed in. Verify your email before submitting.'
      );
    });
  };

  const handleGoogleSignIn = async () => {
    await runAuthAction(async () => {
      const auth = getAuthInstance();
      const credential = await signInWithPopup(auth, createGoogleProvider());
      const verified = Boolean(credential.user.emailVerified);
      setEmailVerified(verified);
      setAuthProvider(resolvePrimaryProvider(credential.user));
      setAuthStatus(
        verified
          ? 'Signed in with Google.'
          : 'Signed in with Google. Use an account with a verified email address.'
      );
    });
  };

  const handleResendVerification = async () => {
    await runAuthAction(async () => {
      const auth = getAuthInstance();
      if (!auth.currentUser) {
        throw new Error('Please sign in first.');
      }
      await sendEmailVerification(auth.currentUser, verificationActionCodeSettings());
      setAuthStatus(`Verification email resent to ${auth.currentUser.email}.`);
    });
  };

  const handleRefreshVerification = async () => {
    await runAuthAction(async () => {
      const auth = getAuthInstance();
      if (!auth.currentUser) {
        throw new Error('Please sign in first.');
      }
      await reload(auth.currentUser);
      const verified = Boolean(auth.currentUser.emailVerified);
      setEmailVerified(verified);
      setAuthStatus(
        verified
          ? 'Email is verified. You can now submit your request.'
          : 'Email is still unverified. Check your inbox/spam and retry.'
      );
    });
  };

  const handleSignOut = async () => {
    await runAuthAction(async () => {
      const auth = getAuthInstance();
      await signOut(auth);
      setEmailVerified(false);
      setAuthProvider('');
      setAuthStatus('Signed out.');
      setRequestSuccess('');
      setRequestError('');
      setRequestDeleting(false);
      setMyRequests([]);
      setMyRequestsError('');
      setMyRequestsLoading(false);
      setHydratedRequestToken('');
      setRequestForm(EMPTY_REQUEST_FORM);
      setTheme(DEFAULT_WIDGET_THEME);
      setCopiedRequestId(null);
    });
  };

  const handleAuthInput = (field, value) => {
    setAuthForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleRequestInput = (field, value) => {
    setRequestForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleRequestedCompetitionToggle = (competition, checked) => {
    const value = String(competition || '').trim();
    if (!value) return;
    setRequestForm((prev) => ({
      ...prev,
      requested_competitions: normalizeCompetitionList(
        checked
          ? [...(Array.isArray(prev.requested_competitions) ? prev.requested_competitions : []), value]
          : (Array.isArray(prev.requested_competitions) ? prev.requested_competitions : []).filter((entry) => entry !== value)
      )
    }));
  };

  const loadCompetitionOptions = useCallback(async () => {
    setCompetitionOptionsLoading(true);
    setCompetitionOptionsError('');
    try {
      const response = await fetch(`${API_BASE}/widget-requests/competition-options`);
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload.error || 'Failed to load leagues/championships.');
      }
      const options = normalizeCompetitionList(Array.isArray(payload.competitions) ? payload.competitions : []);
      setCompetitionOptions(options);
    } catch (err) {
      setCompetitionOptions([]);
      setCompetitionOptionsError(err.message || 'Failed to load leagues/championships.');
    } finally {
      setCompetitionOptionsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCompetitionOptions();
  }, [loadCompetitionOptions]);

  const requestCompetitionOptions = useMemo(() => {
    return normalizeCompetitionList([
      ...FALLBACK_REQUEST_COMPETITIONS,
      ...competitionOptions,
      ...(Array.isArray(requestForm.requested_competitions) ? requestForm.requested_competitions : [])
    ]);
  }, [competitionOptions, requestForm.requested_competitions]);

  const loadMyRequests = async ({ user = firebaseUser, silent = false } = {}) => {
    if (!user || !emailVerified) {
      setMyRequests([]);
      return;
    }
    if (!silent) {
      setMyRequestsLoading(true);
    }
    setMyRequestsError('');
    try {
      const token = await user.getIdToken(true);
      const response = await fetch(`${API_BASE}/widget-requests/mine`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        if (payload.code === 'email_not_verified') {
          setEmailVerified(false);
        }
        throw new Error(payload.error || 'Failed to load your requests.');
      }
      setMyRequests(Array.isArray(payload.requests) ? payload.requests : []);
    } catch (err) {
      setMyRequestsError(err.message || 'Failed to load your requests.');
    } finally {
      if (!silent) {
        setMyRequestsLoading(false);
      }
    }
  };

  useEffect(() => {
    if (!firebaseUser) {
      setMyRequests([]);
      setMyRequestsError('');
      setMyRequestsLoading(false);
      setHydratedRequestToken('');
      return;
    }
    if (!emailVerified) {
      setMyRequests([]);
      setMyRequestsLoading(false);
      setMyRequestsError('Verify your email to view your request status and embed code.');
      setHydratedRequestToken('');
      return;
    }
    loadMyRequests({ user: firebaseUser });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firebaseUser, emailVerified]);

  useEffect(() => {
    const savedRequest = Array.isArray(myRequests) && myRequests.length > 0 ? myRequests[0] : null;
    if (!savedRequest) {
      if (hydratedRequestToken) {
        setHydratedRequestToken('');
      }
      return;
    }
    const token = `${savedRequest.id || ''}:${savedRequest.updated_at || ''}:${savedRequest.status || ''}`;
    if (!token || token === hydratedRequestToken) {
      return;
    }
    setRequestForm({
      website_url: savedRequest.website_url || '',
      embed_page_url: savedRequest.embed_page_url || '',
      use_case: savedRequest.use_case || '',
      requested_competitions: Array.isArray(savedRequest.requested_competitions)
        ? normalizeCompetitionList(savedRequest.requested_competitions)
        : []
    });
    setTheme({
      ...DEFAULT_WIDGET_THEME,
      ...(savedRequest.theme && typeof savedRequest.theme === 'object' ? savedRequest.theme : {})
    });
    setHydratedRequestToken(token);
  }, [myRequests, hydratedRequestToken]);

  const saveWidgetRequest = async ({ submitForApproval }) => {
    setRequestError('');
    setRequestSuccess('');
    if (!firebaseUser) {
      setRequestError('Sign in first.');
      return;
    }
    if (!emailVerified) {
      setRequestError('Email verification is required before saving or submitting.');
      return;
    }
    if (submitForApproval) {
      setRequestSubmitting(true);
    } else {
      setRequestSaving(true);
    }
    try {
      const token = await firebaseUser.getIdToken(true);
      const response = await fetch(`${API_BASE}/widget-requests/mine`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          website_url: requestForm.website_url,
          embed_page_url: requestForm.embed_page_url,
          use_case: requestForm.use_case,
          requested_competitions: requestForm.requested_competitions,
          theme,
          submit_for_approval: submitForApproval
        })
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        if (payload.code === 'email_not_verified') {
          setEmailVerified(false);
        }
        throw new Error(payload.error || (submitForApproval ? 'Failed to submit request.' : 'Failed to save widget settings.'));
      }
      const savedRequest = payload?.request || null;
      if (savedRequest) {
        setMyRequests([savedRequest]);
      }
      if (submitForApproval) {
        setRequestSuccess('Widget request submitted for approval.');
      } else if (String(savedRequest?.status || '').toLowerCase() === 'approved') {
        setRequestSuccess('Widget settings saved. Your embed code is updated and ready to copy.');
      } else {
        setRequestSuccess('Widget settings saved.');
      }
      loadMyRequests({ user: firebaseUser, silent: true });
    } catch (err) {
      setRequestError(err.message || (submitForApproval ? 'Failed to submit request.' : 'Failed to save widget settings.'));
    } finally {
      if (submitForApproval) {
        setRequestSubmitting(false);
      } else {
        setRequestSaving(false);
      }
    }
  };

  const handleSubmitRequest = async (event) => {
    event.preventDefault();
    saveWidgetRequest({ submitForApproval: true });
  };

  const handleSaveRequestDraft = async () => {
    saveWidgetRequest({ submitForApproval: false });
  };

  const handleDeleteMyWidget = async () => {
    const currentRequest = Array.isArray(myRequests) && myRequests.length > 0 ? myRequests[0] : null;
    if (!currentRequest?.id) {
      setRequestError('No widget request found to delete.');
      return;
    }
    if (!firebaseUser) {
      setRequestError('Sign in first.');
      return;
    }
    if (!emailVerified) {
      setRequestError('Email verification is required before deleting.');
      return;
    }
    if (!window.confirm('Delete your widget request? This will stop your current embed from working.')) {
      return;
    }

    setRequestError('');
    setRequestSuccess('');
    setRequestDeleting(true);
    try {
      const token = await firebaseUser.getIdToken(true);
      const response = await fetch(`${API_BASE}/widget-requests/mine`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        if (payload.code === 'email_not_verified') {
          setEmailVerified(false);
        }
        throw new Error(payload.error || 'Failed to delete widget.');
      }

      setMyRequests([]);
      setMyRequestsError('');
      setHydratedRequestToken('');
      setRequestForm(EMPTY_REQUEST_FORM);
      setTheme(DEFAULT_WIDGET_THEME);
      setCopiedRequestId(null);
      setRequestSuccess('Widget deleted.');
    } catch (err) {
      setRequestError(err.message || 'Failed to delete widget.');
    } finally {
      setRequestDeleting(false);
    }
  };

  const copyEmbedCodeForRequest = (request) => {
    const apiKey = String(request?.approved_api_key || '').trim();
    if (!apiKey) {
      return;
    }
    const snippet = WIDGET_SNIPPET(apiKey, buildFeedUrl(), request?.theme || {});
    const markCopied = () => {
      setCopiedRequestId(request.id);
      window.setTimeout(() => {
        setCopiedRequestId(null);
      }, 2000);
    };
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(snippet).then(() => {
        markCopied();
      }).catch(() => {
        window.prompt('Copy the embed code:', snippet);
        markCopied();
      });
      return;
    }
    window.prompt('Copy the embed code:', snippet);
    markCopied();
  };

  const latestRequest = Array.isArray(myRequests) && myRequests.length > 0 ? myRequests[0] : null;
  const latestRequestStatus = String(latestRequest?.status || '').toLowerCase();
  const approvalAlreadyRequested = latestRequestStatus === 'pending';
  const widgetAlreadyApproved = latestRequestStatus === 'approved';
  const requestLocked = !firebaseUser || !emailVerified || requestSubmitting || requestSaving || requestDeleting;
  const requestCompetitionSelectionDisabled = requestLocked || competitionOptionsLoading || requestCompetitionOptions.length === 0;
  const manualVerificationFlow = authProvider === 'password' || !authProvider;

  return (
    <div className="page studio-page">
      <header className="topbar studio-topbar">
        <div>
          <strong>Widget Theme Studio</strong>
          <div className="subtle">Public page for testing banner colors and fonts before requesting your private embed code.</div>
        </div>
      </header>
      <main className="studio-main">
        <section className="studio-section">
          <h2 className="studio-section-title">Theme controls</h2>
          <p className="studio-section-intro">
            This studio uses the same widget renderer as the real embed. Colors and fonts here should match production output.
          </p>

          <div className="studio-panel studio-controls-panel">
            <div className="studio-color-grid">
              {COLOR_FIELDS.map((field) => (
                <label key={field.key} className="studio-field">
                  <span className="studio-field-label">{field.label}</span>
                  <div className="studio-color-input-row">
                    <input
                      type="color"
                      className="studio-color-picker"
                      value={sanitizeHexColor(theme[field.key], DEFAULT_WIDGET_THEME[field.key])}
                      onChange={(e) => updateTheme(field.key, e.target.value)}
                    />
                    <input
                      className="studio-input"
                      value={theme[field.key]}
                      onChange={(e) => updateTheme(field.key, e.target.value)}
                      placeholder="#000000"
                    />
                  </div>
                </label>
              ))}
            </div>

            <div className="studio-font-grid">
              <label className="studio-field">
                <span className="studio-field-label">Base font</span>
                <select
                  className="studio-input studio-select"
                  value={theme.fontBase}
                  onChange={(e) => updateTheme('fontBase', e.target.value)}
                >
                  {FONT_OPTIONS.map((option) => (
                    <option key={`base-${option.label}`} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </label>
              <label className="studio-field">
                <span className="studio-field-label">Title font</span>
                <select
                  className="studio-input studio-select"
                  value={theme.fontTitle}
                  onChange={(e) => updateTheme('fontTitle', e.target.value)}
                >
                  <option value="inherit">Inherit base</option>
                  {FONT_OPTIONS.map((option) => (
                    <option key={`title-${option.label}`} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </label>
              <label className="studio-field">
                <span className="studio-field-label">Team name font</span>
                <select
                  className="studio-input studio-select"
                  value={theme.fontTeam}
                  onChange={(e) => updateTheme('fontTeam', e.target.value)}
                >
                  <option value="inherit">Inherit base</option>
                  {FONT_OPTIONS.map((option) => (
                    <option key={`team-${option.label}`} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </label>
            </div>

            <p className="studio-help-text">
              Font options use local system/web-safe stacks only. The exact typeface shown can vary by device and browser.
            </p>

            <div className="studio-actions">
              <button type="button" onClick={resetTheme}>Reset defaults</button>
            </div>
          </div>
        </section>

        <section className="studio-section">
          <h2 className="studio-section-title">Live preview</h2>
          <div className="studio-preview-shell">
            <iframe
              title="Widget Theme Studio Preview"
              srcDoc={srcDoc}
              className="studio-preview-frame"
            />
          </div>
        </section>

        <section className="studio-section">
          <h2 className="studio-section-title">Request embed code</h2>
          <p className="studio-section-intro">
            Each account has one saved widget configuration. Save your colors and competition selection as a draft any time, then request approval once when ready.
          </p>

          {!authConfigured && (
            <div className="studio-alert studio-alert-error">
              Firebase auth is not configured for this environment, so request submission is unavailable.
            </div>
          )}

          {authConfigured && (
            <div className="studio-stack studio-auth-stack">
              <div className="studio-panel">
                <h3 className="studio-subtitle">Authentication</h3>
                {authLoading && (
                  <p className="studio-help-text">Checking sign-in state…</p>
                )}
                {!authLoading && !firebaseUser && (
                  <div className="studio-stack">
                    <div className="studio-actions">
                      <button type="button" onClick={handleGoogleSignIn} disabled={authBusy}>Continue with Google</button>
                    </div>
                    <div className="studio-auth-divider" role="presentation">or</div>
                    <form onSubmit={handleSignIn} className="studio-form studio-auth-form">
                      <label className="studio-field">
                        <span className="studio-field-label">Email</span>
                        <input
                          className="studio-input"
                          type="email"
                          value={authForm.email}
                          onChange={(e) => handleAuthInput('email', e.target.value)}
                          autoComplete="email"
                          required
                        />
                      </label>
                      <label className="studio-field">
                        <span className="studio-field-label">Password</span>
                        <input
                          className="studio-input"
                          type="password"
                          value={authForm.password}
                          onChange={(e) => handleAuthInput('password', e.target.value)}
                          autoComplete="current-password"
                          minLength={6}
                          required
                        />
                      </label>
                      <div className="studio-actions">
                        <button type="button" onClick={handleCreateAccount} disabled={authBusy}>Create account &amp; send verification</button>
                        <button type="submit" disabled={authBusy}>Sign in</button>
                      </div>
                    </form>
                  </div>
                )}
                {!authLoading && firebaseUser && (
                  <div className="studio-stack">
                    <div className="studio-user-line">
                      Signed in as <strong>{firebaseUser.email}</strong>
                    </div>
                    <div className={emailVerified ? 'studio-message studio-message-success' : 'studio-message studio-message-warning'}>
                      {emailVerified
                        ? 'Email verified.'
                        : (authProvider === 'google.com'
                          ? 'Google account email is not marked verified.'
                          : 'Email not verified yet.')}
                    </div>
                    <div className="studio-actions">
                      {!emailVerified && manualVerificationFlow && (
                        <>
                          <button type="button" onClick={handleResendVerification} disabled={authBusy}>Resend verification email</button>
                          <button type="button" onClick={handleRefreshVerification} disabled={authBusy}>I have verified my email</button>
                        </>
                      )}
                      <button type="button" onClick={handleSignOut} disabled={authBusy}>Sign out</button>
                    </div>
                  </div>
                )}
                {authError && (
                  <div className="studio-message studio-message-error">{authError}</div>
                )}
                {authStatus && (
                  <div className="studio-message studio-message-info">{authStatus}</div>
                )}
              </div>
            </div>
          )}

          <form onSubmit={handleSubmitRequest} className="studio-form studio-request-form">
            <label className="studio-field">
              <span className="studio-field-label">Website where the widget will be used</span>
              <input
                className="studio-input"
                type="url"
                value={requestForm.website_url}
                onChange={(e) => handleRequestInput('website_url', e.target.value)}
                placeholder="https://example.com"
                required
                disabled={requestLocked}
              />
            </label>
            <label className="studio-field">
              <span className="studio-field-label">Page URL where the embed will be placed (optional)</span>
              <input
                className="studio-input"
                type="url"
                value={requestForm.embed_page_url}
                onChange={(e) => handleRequestInput('embed_page_url', e.target.value)}
                placeholder="https://example.com/fixtures"
                disabled={requestLocked}
              />
            </label>
            <div className="studio-field">
              <span className="studio-field-label">Leagues/championships to display (optional)</span>
              <div
                className={`studio-checkbox-list ${requestCompetitionSelectionDisabled ? 'studio-checkbox-list-disabled' : ''}`}
                role="group"
                aria-label="Leagues/championships to display"
              >
                {requestCompetitionOptions.length === 0 && (
                  <div className="studio-help-text">No competitions available</div>
                )}
                {requestCompetitionOptions.map((competition) => (
                  <label key={competition} className="studio-checkbox-option">
                    <input
                      type="checkbox"
                      checked={requestForm.requested_competitions.includes(competition)}
                      onChange={(e) => handleRequestedCompetitionToggle(competition, e.target.checked)}
                      disabled={requestCompetitionSelectionDisabled}
                    />
                    <span>{competition}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="studio-help-text">
              {competitionOptionsLoading
                ? 'Loading leagues/championship list…'
                : 'Leave this list unselected to request all competitions.'}
            </div>
            {competitionOptionsError && (
              <div className="studio-message studio-message-error">
                {competitionOptionsError}
              </div>
            )}
            <label className="studio-field">
              <span className="studio-field-label">Why do you want this widget on your website?</span>
              <textarea
                className="studio-input studio-textarea"
                value={requestForm.use_case}
                onChange={(e) => handleRequestInput('use_case', e.target.value)}
                placeholder="Tell us a bit more about yourself, your website where you want to use the widget and why you would like to have use it"
                minLength={10}
                rows={4}
                required
                disabled={requestLocked}
              />
            </label>
            <div className="studio-help-text">
              Your current theme settings are saved with this widget configuration.
            </div>
            <div className="studio-actions">
              <button type="button" onClick={handleSaveRequestDraft} disabled={requestLocked}>
                {requestSaving
                  ? 'Saving…'
                  : (widgetAlreadyApproved ? 'Save widget changes' : 'Save widget draft')}
              </button>
              {!widgetAlreadyApproved && (
                <button type="submit" disabled={requestLocked || approvalAlreadyRequested}>
                  {requestSubmitting
                    ? 'Submitting request…'
                    : (approvalAlreadyRequested ? 'Approval requested' : 'Submit request for approval')}
                </button>
              )}
            </div>
            {requestError && (
              <div className="studio-message studio-message-error">{requestError}</div>
            )}
            {requestSuccess && (
              <div className="studio-message studio-message-success">{requestSuccess}</div>
            )}
          </form>

          <div className="studio-my-requests">
            <h3 className="studio-subtitle">My widget</h3>
            {!firebaseUser && (
              <div className="studio-help-text">
                Sign in to view your saved widget settings and approval status.
              </div>
            )}
            {firebaseUser && !emailVerified && (
              <div className="studio-message studio-message-warning">
                Verify your email to view your request status and embed code.
              </div>
            )}
            {firebaseUser && emailVerified && (
              <div className="studio-stack">
                <div className="studio-actions">
                  <button type="button" onClick={() => loadMyRequests({ user: firebaseUser })} disabled={myRequestsLoading}>
                    {myRequestsLoading ? 'Loading…' : 'Reload my widget'}
                  </button>
                  <button
                    type="button"
                    onClick={handleDeleteMyWidget}
                    disabled={myRequestsLoading || requestDeleting || myRequests.length === 0}
                  >
                    {requestDeleting ? 'Deleting…' : 'Delete my widget'}
                  </button>
                </div>
                {myRequestsError && (
                  <div className="studio-message studio-message-error">{myRequestsError}</div>
                )}
                {!myRequestsLoading && myRequests.length === 0 && (
                  <div className="studio-help-text">No widget saved for this account yet.</div>
                )}
                {myRequests.length > 0 && (
                  <div className="studio-table-wrap">
                    <table className="studio-table">
                      <thead>
                        <tr>
                          <th>Widget</th>
                          <th>Website</th>
                          <th>Status</th>
                          <th>Review notes</th>
                          <th>Embed</th>
                        </tr>
                      </thead>
                      <tbody>
                        {myRequests.map((request) => {
                          const status = String(request.status || 'pending').toLowerCase();
                          const statusClass = status === 'approved'
                            ? 'studio-status-badge studio-status-approved'
                            : status === 'rejected'
                              ? 'studio-status-badge studio-status-rejected'
                              : 'studio-status-badge studio-status-pending';
                          const statusLabel = status === 'draft' ? 'draft' : status;

                          return (
                            <tr key={request.id}>
                              <td>
                                <div>#{request.id || '—'}</div>
                                <div className="studio-muted">
                                  {request.created_at ? new Date(request.created_at).toLocaleString() : '—'}
                                </div>
                                {Array.isArray(request.requested_competitions) && request.requested_competitions.length > 0 && (
                                  <div className="studio-muted studio-competitions">
                                    Competitions: {request.requested_competitions.join(', ')}
                                  </div>
                                )}
                              </td>
                              <td>
                                {request.website_url
                                  ? <a href={request.website_url} target="_blank" rel="noreferrer">{request.website_url}</a>
                                  : '—'}
                              </td>
                              <td>
                                <span className={statusClass}>
                                  {statusLabel}
                                </span>
                              </td>
                              <td className="studio-table-notes">
                                {request.admin_notes || '—'}
                              </td>
                              <td>
                                {request.approved_api_key ? (
                                  <div className="studio-inline-actions">
                                    <button type="button" onClick={() => copyEmbedCodeForRequest(request)}>
                                      Copy embed code
                                    </button>
                                    {copiedRequestId === request.id && (
                                      <span className="studio-message studio-message-success studio-copy-label">Copied</span>
                                    )}
                                  </div>
                                ) : (
                                  <span className="studio-muted">Available after approval</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        </section>
      </main>
    </div>
  );
};

export default WidgetThemeStudio;
